/**
 * Drag coefficient lookup by vane geometry × velocity regime.
 *
 * Cd values are calibrated against published vane comparison data
 * (Easton + Iron Will white papers) and the documented field anchors
 * in the v140 calibration set (Field Notes appendix).
 */

import type { VaneGeometry } from './types';

interface DragRow {
  geometry: VaneGeometry;
  cdBaseline: number;
  cdSpeedExponent: number;
}

const DRAG_TABLE: ReadonlyArray<DragRow> = [
  { geometry: 'DCA Sabre',       cdBaseline: 0.32, cdSpeedExponent: -0.04 },
  { geometry: 'Aerovane 2.2',     cdBaseline: 0.34, cdSpeedExponent: -0.03 },
  { geometry: 'AAE Talon',        cdBaseline: 0.36, cdSpeedExponent: -0.02 },
  { geometry: 'AAE Max Stealth',  cdBaseline: 0.38, cdSpeedExponent: -0.01 },
  { geometry: 'Bohning Blazer',   cdBaseline: 0.40, cdSpeedExponent:  0.00 },
  { geometry: 'Other',            cdBaseline: 0.40, cdSpeedExponent:  0.00 },
];

const BASELINE_SPEED_FPS = 250;

/*
 * Fletched-arrow drag calibration.
 *
 * The cdBaseline values above are referenced to the SHAFT frontal area, but a
 * fletched arrow's drag is dominated by the vanes — so shaft-referenced cd
 * alone produces ~8x too little drag (the arrow barely decelerated: 97.8% KE
 * retained at 60yd, vs ~80% in reality). This constant scales the
 * shaft-referenced cd up to the fletched-arrow effective drag.
 *
 * Anchored to chronograph data (Sal, 2026-06): a 460gr arrow launched at
 * 294fps chronos 262fps at 75yd (~10.9% velocity loss / ~79% KE retained).
 * Tuned so the integrator reproduces that velocity decay.
 */
const FLETCHED_DRAG_CALIBRATION = 7.4;

export function dragCoefficient(geometry: VaneGeometry, speedFps: number, offsetDeg: number): number {
  const row = DRAG_TABLE.find((r) => r.geometry === geometry) ?? DRAG_TABLE[DRAG_TABLE.length - 1];
  const speedScale = Math.pow(speedFps / BASELINE_SPEED_FPS, row.cdSpeedExponent);
  /* 4° offset = ~7% more drag than 0° — linear approximation in low-offset range. */
  const offsetScale = 1 + 0.018 * offsetDeg;
  return row.cdBaseline * speedScale * offsetScale * FLETCHED_DRAG_CALIBRATION;
}
